import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { useConfirm } from '@shared/components/ConfirmDialog';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { languagesApi } from '@api/languages';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { LanguageFormModal } from '../components/LanguageFormModal';
import type { Language, LanguageCreateInput, LanguageUpdateInput } from '../types';

const { Text } = Typography;

export function LanguagesPage() {
  const { t } = useTranslation(['languages', 'common']);
  const qc = useQueryClient();
  const confirm = useConfirm();
  const canManage = useCan(PERMISSIONS.SETTINGS_MANAGE);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Language | null>(null);

  const { data: languages = [], isLoading } = useQuery({
    queryKey: ['languages'],
    queryFn: () => languagesApi.list(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['languages'] });

  const createMutation = useMutation({
    mutationFn: (input: LanguageCreateInput) => languagesApi.create(input),
    onSuccess: async () => {
      toast.success(t('toast.created'));
      setModalOpen(false);
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; input: LanguageUpdateInput }) =>
      languagesApi.update(vars.id, vars.input),
    onSuccess: async () => {
      toast.success(t('toast.updated'));
      setModalOpen(false);
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => languagesApi.remove(id),
    onSuccess: async () => {
      toast.success(t('toast.deleted'));
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (lang: Language) => {
    setEditing(lang);
    setModalOpen(true);
  };

  const requestDelete = (lang: Language) => {
    void (async () => {
      const ok = await confirm({
        title: t('delete.title'),
        description: t('delete.confirm', { name: lang.name, code: lang.code }),
        okText: t('delete.ok'),
      });
      if (ok) deleteMutation.mutate(lang.id);
    })();
  };

  const columns: ColumnsType<Language> = [
    {
      title: t('columns.code'),
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => <Text code>{code}</Text>,
    },
    { title: t('columns.name'), dataIndex: 'name', key: 'name' },
    {
      title: t('columns.default'),
      key: 'default',
      render: (_, r) =>
        r.isDefault ? <Tag color="gold">{t('badge.default')}</Tag> : null,
    },
    {
      title: t('columns.active'),
      key: 'active',
      render: (_, r) =>
        r.isActive ? (
          <Tag color="green">{t('common:states.active')}</Tag>
        ) : (
          <Tag>{t('badge.inactive')}</Tag>
        ),
    },
    { title: t('columns.order'), dataIndex: 'displayOrder', key: 'order' },
    {
      title: t('columns.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (v: string) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—'),
    },
    {
      title: t('columns.actions'),
      key: 'actions',
      align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<Pencil size={15} />}
            onClick={() => openEdit(r)}
            disabled={!canManage}
            aria-label={t('form.editTitle')}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<Trash2 size={15} />}
            onClick={() => requestDelete(r)}
            // Varsayılan dil silinemez (BE LANGUAGE_DEFAULT_IMMUTABLE) — proaktif engelle.
            disabled={!canManage || r.isDefault}
            loading={deleteMutation.isPending && deleteMutation.variables === r.id}
            aria-label={t('delete.title')}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('title') },
        ]}
        actions={
          canManage ? (
            <Button type="primary" icon={<Plus size={16} />} onClick={openCreate}>
              {t('new')}
            </Button>
          ) : null
        }
      />

      <Card>
        <Table<Language>
          rowKey="id"
          columns={columns}
          dataSource={languages}
          loading={isLoading}
          pagination={false}
          locale={{ emptyText: t('empty') }}
        />
      </Card>

      <LanguageFormModal
        open={modalOpen}
        editing={editing}
        saving={createMutation.isPending || updateMutation.isPending}
        onCancel={() => setModalOpen(false)}
        onCreate={(input) => createMutation.mutate(input)}
        onUpdate={(id, input) => updateMutation.mutate({ id, input })}
      />
    </div>
  );
}
